import { buildTranslate } from 'locales'
import { formatRewardValue } from '../rewardValue'
import type { RewardMultiSelectLabels } from './index'

// Bound once to the single shared namespace. The reward-picker strings live in
// ONE top-level block (`recovery_ai.reward_picker`, mirroring `brand_voice`),
// so this helper — not the consumers — owns where they're resolved from. The
// Settings pane and the wizard card pass only the count (AI-91 acceptance: one
// locale source for the shared strings).
const t = buildTranslate('recovery_ai.reward_picker')

/**
 * Builds the {@link RewardMultiSelectLabels} object both consumers (Settings
 * `ApprovedRewardsPane` and wizard `ApprovedRewardsCard`) feed to
 * `RewardMultiSelect`. Resolves every string from the shared
 * `recovery_ai.reward_picker` block internally, so neither consumer touches the
 * picker copy directly and the two surfaces can never drift again.
 *
 * react-i18nify does NOT auto-pluralize, so the `_one`/`_other` form is picked
 * here by count (the locked P4 plural mechanic); the component stays
 * i18n-agnostic and renders the resolved string verbatim.
 */
export const buildRewardMultiSelectLabels = (
  selectedCount: number
): RewardMultiSelectLabels => ({
  selectRewards: t('select_rewards'),
  rewardsSelected: t(
    selectedCount === 1 ? 'rewards_selected_one' : 'rewards_selected_other',
    { count: selectedCount }
  ),
  selectedRewardsLabel: t('selected_rewards_label'),
  // Accepts null: AvailableReward.label is nullable on the wire, and the
  // fallback belongs here rather than at the call site so the component stays
  // i18n-agnostic (as with the plural forms above). A nameless reward's remove
  // button reads "Remove this reward" instead of a bare "Remove ".
  removeRewardAriaLabel: (name: string | null) =>
    t('remove_reward_aria_label', { name: name || t('unnamed_reward') }),
  noRewardsAvailable: t('no_rewards_available'),
  createRewardCta: t('create_reward_cta'),
  // Selected-chip collapse toggle. A builder (like removeRewardAriaLabel) so the
  // per-count %{count} plural form is picked here — the component stays i18n-agnostic.
  viewMoreRewards: (count: number) =>
    t(count === 1 ? 'view_more_one' : 'view_more_other', { count }),
  showLessRewards: t('show_less'),
  // Resolves from the shared `recovery_ai.reward_value` block (NOT
  // `reward_picker` above) — the make-good ladder shares this exact string,
  // in a different i18n namespace, so it has to live outside this helper's
  // own `t` binding (see rewardValue.ts).
  rewardValue: formatRewardValue,
})
